import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Center,
  RadioGroup,
  Stack,
  Radio,
  Text,
} from '@chakra-ui/react';
import { Button, ColorScheme } from 'components/Button';
import { AuthContext } from 'auth/AuthContextProvider';
import { useContext, useState } from 'react';
import { Input } from 'components/Input';

export type Props = {
  titre: string;
  description: string;
  avertissement: string;
  textButtonValider: string;
  minimumValue?: number;
  onValidate: (value: any) => void;
  colorButtonValider: string;
  idDossier: number;
  type: string;
} & Omit<ModalProps, 'children'>;

export const ConfirmationModalWithInput = ({
  titre,
  description,
  avertissement,
  textButtonValider,
  minimumValue,
  onValidate,
  idDossier,
  type,
  colorButtonValider = 'teal',
  ...props
}: Props) => {
  const { token } = useContext(AuthContext);
  const [value, setValue] = useState(0);
  const [projet_contrat_signe, setProjetContratSigne] = useState('');

  const ValueCorrect =
    !Number.isNaN(value) &&
    (minimumValue ? value >= minimumValue : true) &&
    projet_contrat_signe !== '';

  const closeModal = () => {
    setProjetContratSigne('');
    setValue(0);
    props.onClose();
  };

  const handleValidate = () => {
    onValidate(value);
    // Le retour de l'API est affiché dans une modal de résultat dédiée (ResultModal).
    closeModal();
  };

  const contratLabel =
    type === 'LOGEMENT_ANCIEN'
      ? 'Offre ou compromis de vente signé'
      : type === 'LOGEMENT_NEUF'
      ? 'Contrat de réservation signé'
      : 'Contrat ou devis signé';

  const setValueMaturite = (e: any) => {
    setProjetContratSigne(e);
    fetch('/API/dossiers/enr/' + idDossier, {
      method: 'POST',
      body: JSON.stringify({ contrat_signe: e }),
      headers: new Headers({
        Authorization: token,
        'Content-Type': 'application/json',
      }),
    }).then((res) => res.json());
  };

  return (
    <Modal {...props} onClose={closeModal} scrollBehavior="inside" size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
        <ModalCloseButton />
        <ModalHeader pt={8} pb={2}>
          <Center>
            <Text color="teal.900" fontSize="2xl" fontWeight="extrabold" textAlign="center">
              {titre}
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody textAlign="center" px={8} pb={10}>
          <Box color="gray.600">
            <FormatText text={description} />
          </Box>

          <Box mt={5} textAlign="left">
            <Text fontSize="sm" fontWeight="semibold" color="teal.900" mb={2}>
              Montant total du projet
            </Text>
            <Input onChange={(e) => setValue(parseInt(e.currentTarget.value))} info={'€'} />
            {minimumValue && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                Doit être supérieur à {minimumValue.toLocaleString('fr-FR')} €
              </Text>
            )}
          </Box>

          <Box mt={6}>
            <Text fontSize="sm" fontWeight="semibold" color="teal.900" mb={2}>
              {contratLabel}
            </Text>
            <RadioGroup onChange={setValueMaturite} value={projet_contrat_signe}>
              <Center direction="row">
                <Radio value="true">Oui</Radio>
                <Radio ml={6} value="false">
                  Non
                </Radio>
              </Center>
            </RadioGroup>
          </Box>

          {avertissement && (
            <Box bg="red.50" borderRadius="lg" px={5} py={3} mt={6}>
              <Box fontWeight="bold" color="red.500" fontSize="sm">
                <FormatText text={avertissement} />
              </Box>
            </Box>
          )}

          <Stack direction="row" spacing={4} mt={8}>
            <Button colorScheme="gray" onClick={closeModal} flex={1}>
              Fermer
            </Button>
            <Button
              disabled={!ValueCorrect}
              colorScheme={colorButtonValider as ColorScheme}
              onClick={handleValidate}
              flex={1}
            >
              {textButtonValider}
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

function FormatText(props: { text: string }): JSX.Element {
  const textToFormat = props.text;
  return (
    <>
      {textToFormat.split('\\n').map((str, i) => (
        <p key={i}>{str}</p>
      ))}
    </>
  );
}
